export const modelTypes = ['Protein Structure', 'Language Model', 'Molecular Dynamics', 'Deep Learning'] as const
export type ModelType = (typeof modelTypes)[number]

export enum ModelMethod {
  alphafold = 'AlphaFold',
  boltz = 'Boltz',
  esm = 'ESM',
  evolla = 'Evolla',
  protrek = 'ProTrek',
  denovo = 'DeNovo',
  saprot = 'SaProt',
  signalp = 'SignalP',
  ampnet = 'AMP-Net',
  deeploc = 'DeepLoc',
  openmm = 'OpenMM',
  simplefold = 'SimpleFold'
}

export interface Model<Type = string> {
  id: string
  name: string
  label: string
  method: string
  type: Type
  categories: string[]
  description: string
  version?: string
  citation?: string
  paperLink?: string
}

export const taxonomyCategories = [
  'Protein Folding',
  'Molecular Dynamics',
  'Protein Design',
  'Protein Localization',
  'Protein Solubility',
  'Protein Stability',
  'Signal Peptide Detection',
  'Toxicity Prediction',
  'Pharmacokinetics',
  'Antibody affinity',
  'Protein–protein interaction',
  'Drug–target interaction'
] as const

export const models: Model<ModelType>[] = [
  {
    id: 'alphafold-2',
    name: 'AlphaFold 2',
    label: 'AlphaFold 2',
    method: ModelMethod.alphafold,
    type: 'Protein Structure',
    categories: ['Protein Folding'],
    description:
      'State-of-the-art protein structure predictor with benchmark-setting accuracy on monomers and broad adoption across biology.',
    version: 'v2.3.1',
    citation: 'Jumper et al., Nature (2021)',
    paperLink: 'https://www.nature.com/articles/s41586-021-03819-2'
  },
  {
    id: 'boltz-1',
    name: 'Boltz-1',
    label: 'Boltz-1',
    method: ModelMethod.boltz,
    type: 'Protein Structure',
    categories: ['Protein–protein interaction', 'Drug–target interaction'],
    description:
      'Biomolecular interaction modeling for protein complexes and small-molecule binding; joint structure and affinity inference.',
    version: 'v1.0',
    citation: 'Wohlwend et al., bioRxiv (2024)',
    paperLink: 'https://www.biorxiv.org/content/10.1101/2024.11.19.624167v1'
  },
  {
    id: 'boltz-2',
    name: 'Boltz-2',
    label: 'Boltz-2',
    method: ModelMethod.boltz,
    type: 'Protein Structure',
    categories: ['Protein–protein interaction', 'Drug–target interaction', 'Protein Design'],
    description:
      'Successor to Boltz-1 with improved interaction modeling for complex structure and binding tasks; supports design workflows via scoring.',
    version: '2.2.1',
    citation: 'Passaro et al., bioRxiv (2025)',
    paperLink: 'https://www.biorxiv.org/content/10.1101/2025.06.14.659707v1'
  },
  {
    id: 'simple-fold',
    name: 'SimpleFold',
    label: 'SimpleFold',
    method: ModelMethod.simplefold,
    type: 'Protein Structure',
    categories: ['Protein Folding'],
    description:
      'First flow-matching based protein folding model using general purpose transformer blocks. Available in sizes from 100M to 3B parameters, challenges complex domain-specific architectures with competitive performance on standard benchmarks while enabling efficient deployment on consumer hardware.',
    version: 'v1.0',
    citation: 'Wang et al., arXiv (2025)',
    paperLink: 'https://arxiv.org/abs/2509.18480'
  },
  {
    id: 'esm-1b',
    name: 'ESM-1b',
    label: 'ESM-1b',
    method: ModelMethod.esm,
    type: 'Language Model',
    categories: ['Protein Folding', 'Protein Design'],
    description:
      'Evolution-scale protein language model family; enables fast structure prediction (ESMFold) and inverse folding for design (ESM-IF1).',
    version: 'v1.0.3',
    citation: 'Lin et al., Science (2023)',
    paperLink: 'https://www.science.org/doi/10.1126/science.ade2574'
  },
  {
    id: 'evolla-llama3-8b',
    name: 'EvoLLa + Llama3 8B',
    label: 'EvoLLa + Llama3 8B',
    method: ModelMethod.evolla,
    type: 'Language Model',
    categories: [
        'Protein Localization',
        'Protein Solubility',
        'Protein–protein interaction',
        'Drug–target interaction'
    ],
    description:
        'LLM adapted to protein property prediction with verbalized benchmarks, covering solubility, localization, PPI, and DTI tasks.',
    version: 'v1.0',
    citation: 'Chen et al., bioRxiv (2025)',
    paperLink: 'https://www.biorxiv.org/content/10.1101/2025.01.05.630192v2'
    },
  {
    id: 'protrek',
    name: 'ProTrek',
    label: 'ProTrek',
    method: ModelMethod.protrek,
    type: 'Deep Learning',
    categories: ['Protein Design'],
    description:
      'Tri-modal contrastive representations (sequence–structure–function) to navigate protein space and support design/annotation.',
    version: 'v1.0',
    citation: 'Su et al., bioRxiv (2024)',
    paperLink: 'https://www.biorxiv.org/content/10.1101/2024.05.30.596740v2'
  },
  {
    id: 'denovo-pinal',
    name: 'DeNovo-Pinal',
    label: 'DeNovo-Pinal',
    method: ModelMethod.denovo,
    type: 'Protein Structure',
    categories: ['Protein Design'],
    description:
      'De novo protein design guided by natural-language intent, generating candidates consistent with target specifications.',
    version: 'v1.0',
    citation: 'Dai et al., bioRxiv (2024)',
    paperLink: 'https://www.biorxiv.org/content/10.1101/2024.08.01.606258v1'
  },
  {
    id: 'saprot',
    name: 'SaProt',
    label: 'SaProt',
    method: ModelMethod.saprot,
    type: 'Protein Structure',
    categories: ['Protein Design'],
    description:
      'Structure-aware protein language model with 3D-informed vocabulary that improves downstream property modeling and redesign.',
    version: 'v1.0',
    citation: 'Su et al., ICLR (2024)',
    paperLink: 'https://openreview.net/forum?id=6MRm3G4NiU'
  },
  {
    id: 'signalp-6-0',
    name: 'SignalP 6.0',
    label: 'SignalP 6.0',
    method: ModelMethod.signalp,
    type: 'Deep Learning',
    categories: ['Signal Peptide Detection', 'Protein Localization'],
    description:
      'Signal peptide prediction across organism groups, including type classification and cleavage site identification.',
    version: 'v6.0',
    citation: 'Teufel et al., Nat. Biotechnol. (2022)',
    paperLink: 'https://www.nature.com/articles/s41587-021-01156-3'
  },
  {
    id: 'amp-net',
    name: 'AMP-Net',
    label: 'AMP-Net',
    method: ModelMethod.ampnet,
    type: 'Deep Learning',
    categories: ['Protein Design'],
    description:
      'Deep learning framework for antimicrobial peptide discovery and prioritization to accelerate therapeutics design.',
    version: 'v2.0',
    citation: 'Ruiz Puentes et al., Membranes (2022)',
    paperLink: 'https://www.mdpi.com/2077-0375/12/7/708'
  },
  {
    id: 'deeploc',
    name: 'DeepLoc',
    label: 'DeepLoc',
    method: ModelMethod.deeploc,
    type: 'Deep Learning',
    categories: ['Protein Localization'],
    description:
      'Deep learning model for protein subcellular localization; widely used baseline (DeepLoc 2.0).',
    version: 'v2.0',
    citation: 'Thumuluri et al., Nucleic Acids Res. (2022)',
    paperLink: 'https://academic.oup.com/nar/article/50/W1/W228/6576357'
  },
  {
    id: 'openmm',
    name: 'OpenMM',
    label: 'OpenMM',
    method: ModelMethod.openmm,
    type: 'Molecular Dynamics',
    categories: ['Molecular Dynamics'],
    description:
      'High-performance molecular dynamics engine for biomolecular simulation; GPU-accelerated and extensible.',
    version: 'v8.0',
    citation: 'Eastman et al., J. Phys. Chem. B (2024)',
    paperLink: 'https://pubs.acs.org/doi/10.1021/acs.jpcb.3c06662'
  }
]